import { uuid } from "@/utils/string.ts";
import { EventsItem } from "@/types";
import moment from "moment-timezone";
import cn from "classnames";
import EventItem from "@/components/agenda/desktop/month/EventItem.tsx";
import { useTimezone } from "@/contexts/TimezoneContext";
import { useMemo } from "react";

interface DateItemProps {
  id: string;
  type: "past" | "now" | "future";
  value: moment.Moment;
  events: EventsItem[] | null;
}

const DateItem = ({ id, type, value, events }: DateItemProps) => {
  const { selectedTimezone } = useTimezone();
  
  // Memoize the current date and isToday check
  const dateReference = useMemo(() => {
    const currentDate = moment().tz(selectedTimezone);
    const isTodayValue = currentDate.isSame(value, "day");
    return { currentDate, isTodayValue };
  }, [selectedTimezone, value]);

  // Memoize filtered events
  const filteredEvents = useMemo(() => {
    if (!events) return null;
    return events.filter(event => 
      moment(event.data).tz(selectedTimezone).isSame(value.format("YYYY-MM-DD"))
    );
  }, [events, selectedTimezone, value]);

  return (
    <div
      id={id}
      className={cn(
        "px-1 py-1  rounded-md flex flex-col   gap-2  @container h-full",
        {
          "text-brand": type === "now",
          "text-light-900": ["past", "future"].includes(type),
          "bg-[#56438A]": dateReference.isTodayValue,
          "bg-white": !dateReference.isTodayValue,
        },
      )}
    >
      <div
        data-show={`${
          value.date() === 1 && type !== "now" && !dateReference.isTodayValue
        }`}
        className="text-base font-semibold flex flex-row gap-x-2 justify-center items-center"
      >
        <p
          className={cn(
            "h-8 w-8  inline-flex items-center justify-center rounded-full",
            { "!text-light  rounded-full": dateReference.isTodayValue },
          )}
        >
          {value.format("D")}
        </p>

        {value.date() === 1 &&
          type !== "now" &&
          !dateReference.isTodayValue && <p>{value.format("MMM")}</p>}
      </div>

      <div className="flex flex-col w-full gap-y-1">
        {filteredEvents?.map((event) => (
          <EventItem key={uuid()} {...event} />
        ))}
      </div>
    </div>
  );
};

export default DateItem;
